import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Collapse, CollapseProps, Modal, Skeleton } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DownOutlined } from '@ant-design/icons';
import { useStores } from '@/hooks/use-stores';
import { usePreviousValue } from '@/hooks/usePreviousValue';
import { EcoSelectStation } from '@/comp';
import { permissions } from '@/constants';
import StationStages from '@/comp/StationStages/StationStages';
import { getColorByStageOrder } from '@/comp/StationStages/utils';
import { StageHeader } from './Stage/panel/StagePanel';
import { StageProvider } from './Stage/Provider';
import LifecycleStage from './Stage/content/LifecycleStage';
import { StyledLifecyclePage } from './styled';
import { useDayjsLocal } from './hooks/use-dayjs-local';

const LifecyclePage = observer(() => {
  const { t } = useTranslation();
  const { lifecycleStore, permissionStore } = useStores();
  const { stages, currentStage, isError, isFetching } = lifecycleStore;
  const [isLoading, setIsLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prevStageId = usePreviousValue(currentStage?.id);
  const stageIdFromParams = searchParams.get('stage');
  const [activeKeys, setActiveKeys] = useState<string[]>([stageIdFromParams || currentStage?.id.toString()]);
  const hasViewPermission = permissionStore.isAuth(permissions.VIEW_STATION_STAGE_LIFECYCLE);

  const stagesItems: CollapseProps['items'] = stages.map((stage, index) => ({
    key: stage.id,
    extra: (
      <StageProvider stage={stage}>
        <StageHeader />
      </StageProvider>
    ),
    styles: { header: { color: getColorByStageOrder(index + 1) as string } },
    children: (
      <StageProvider stage={stage}>
        <LifecycleStage />
      </StageProvider>
    ),
    collapsible: stage.isDisabled ? 'disabled' : 'icon',
  }));

  useEffect(() => {
    if (!hasViewPermission) {
      navigate('/home');
    }
  }, [hasViewPermission]);

  useEffect(() => {
    if (prevStageId && currentStage?.id !== prevStageId) {
      setActiveKeys([currentStage?.id.toString()]);
    } else {
      setActiveKeys([stageIdFromParams || currentStage?.id.toString()]);
    }
  }, [currentStage]);

  useEffect(() => {
    if (stages.length === 0) {
      setIsLoading(true);
      lifecycleStore.getStages().finally(() => {
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [stages]);

  useEffect(() => {
    if (isError) {
      Modal.error({
        title: t('Error'),
        content: t('No stages found'),
      });
    }
  }, [isError]);

  useDayjsLocal();

  return (
    <StyledLifecyclePage>
      <div className="top-row">
        <EcoSelectStation requiredPermission={permissions.VIEW_STATION_VALUE_DASHBOARD} />
        <StationStages />
      </div>
      <span className="lifecycle-page-title">{t('Station tracker')}</span>
      <Skeleton active paragraph={{ rows: 5, width: '100%' }} title={false} loading={isLoading || isFetching}>
        <div className="lifecycle-container">
          <Collapse
            onChange={keys => {
              setActiveKeys(keys);
            }}
            className="stages-container"
            bordered={false}
            activeKey={activeKeys}
            expandIcon={({ isActive }) => (
              <Button size="large" type="dashed" icon={<DownOutlined rotate={isActive ? 180 : 0} />} />
            )}
            expandIconPosition="end"
            items={stagesItems}
          />
        </div>
      </Skeleton>
    </StyledLifecyclePage>
  );
});

export default LifecyclePage;
